import { useState } from 'react';
import { Eye, EyeOff, Trash, MoreVertical } from 'lucide-react';
import { DeleteConfirmationModal } from '@/components/delete-confirmation-modal';
import { Key } from '@/types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotification } from '@/components/notifications';
import { Card, CardContent } from '@/components/ui/card';
import { AddCapitolKeyModal } from './add-capitol-key';
import { formatDate } from '@/utils/format-date';
import { CopyButton } from '@/components/ui/copy-button';
import { useDeleteCapitolKey } from '@/hooks/mutations/keys/use-delete-capitol-key';
import { useQueryClient } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/org-store';

interface KeyItemProps {
  apiKey: Key;
}

const iconClassNames =
  'cursor-pointer stroke-zinc-500 hover:stroke-zinc-900 transition duration-200 ease-in-out';

export const CapitolKeyItem = ({ apiKey }: KeyItemProps) => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid || '';
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCapitolApikeyModalOpen, setIsCapitolApikeyModalOpen] =
    useState(false);
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const mask = '******************************************************';

  const { mutate: deleteCapitolKey, isPending } = useDeleteCapitolKey({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['capitol-keys', orgId],
      });
      setIsConfirmationModalOpen(false);
      notification.success('Key was deleted successfully');
    },
    onError: (error) => {
      notification.error(error.message || 'An unexpected error occurred');
    },
  });

  return (
    <>
      <Card className='relative'>
        <CardContent className='flex justify-between'>
          <div className='flex flex-col justify-between gap-2 w-full'>
            <h5 className='font-bold tracking-tight text-zinc-900 dark:text-zinc-50'>
              {apiKey?.name}
            </h5>

            <p className='text-sm text-zinc-700 dark:text-zinc-300'>
              {apiKey?.description}
            </p>
            <div className='flex items-center gap-4'>
              <p
                id={`api-keys-item-key-${apiKey?.id || 'unknown'}`}
                className='text-sm text-zinc-700 dark:text-zinc-300'
              >
                {isKeyVisible ? apiKey?.api_key : mask}
              </p>
              <div className='flex'>
                <Button
                  id={`api-keys-item-toggle-visibility-${apiKey?.id || 'unknown'}`}
                  size='icon'
                  variant='ghost'
                  onClick={() => setIsKeyVisible(!isKeyVisible)}
                >
                  {isKeyVisible ? (
                    <EyeOff size={16} className={iconClassNames} />
                  ) : (
                    <Eye size={16} className={iconClassNames} />
                  )}
                </Button>
                <CopyButton
                  text={apiKey?.api_key || ''}
                  size='default'
                  ariaLabel='Copy API key'
                />
              </div>
              {apiKey?.created_at && (
                <p className='text-xs text-zinc-500 dark:text-zinc-400 ml-auto mt-auto'>
                  Created: {formatDate(apiKey.created_at)}
                </p>
              )}
            </div>
          </div>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                id={`api-keys-item-menu-${apiKey?.id || 'unknown'}`}
                variant='ghost'
                size='icon'
                className='absolute right-2 top-2'
              >
                <MoreVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                id={`api-keys-item-menu-delete-${apiKey?.id || 'unknown'}`}
                variant='destructive'
                onClick={() => {
                  setIsDropdownOpen(false);
                  setIsConfirmationModalOpen(true);
                }}
                className='text-destructive'
              >
                <Trash className='h-4 w-4' />
                Delete Key
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardContent>
      </Card>
      <DeleteConfirmationModal
        isOpen={isConfirmationModalOpen}
        isLoading={isPending}
        title='Delete API key'
        description='Are you sure you want to delete the key? This action cannot be undone.'
        buttonLabel='Delete key'
        handleDelete={() => deleteCapitolKey({ id: apiKey.id })}
        handleCancel={() => setIsConfirmationModalOpen(false)}
      />
      <AddCapitolKeyModal
        keyId={apiKey?.id}
        key={apiKey?.api_key}
        description={apiKey?.description}
        isOpen={isCapitolApikeyModalOpen}
        setIsApikeyModalOpen={setIsCapitolApikeyModalOpen}
      />
    </>
  );
};
